import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { projectsApi, deckApi } from "@/lib/api";
import { formatCurrency, ITEM_LABELS, ITEM_UNITS, PAINT_TYPES, PREP_LEVELS, DECK_CATEGORIES } from "@/lib/utils";

export default function DeckEditor() {
  const { projectId, deckId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [deck, setDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [estimate, setEstimate] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [projectId, deckId]);

  const loadData = async () => {
    try {
      const projectRes = await projectsApi.getById(projectId);
      setProject(projectRes.data);
      const foundDeck = projectRes.data.deck_areas?.find(d => d.id === deckId);
      if (!foundDeck) { toast.error("Deck area not found"); navigate(`/project/${projectId}`); return; }
      setDeck(foundDeck);
      const estimateRes = await projectsApi.getEstimate(projectId);
      setEstimate(estimateRes.data);
    } catch (error) {
      toast.error("Failed to load data");
      navigate(`/project/${projectId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDimensionChange = useCallback((field, value) => {
    const numValue = parseFloat(value) || 0;
    setDeck(prev => {
      const updated = { ...prev, [field]: numValue };
      const length = field === 'length' ? numValue : prev.length;
      const width = field === 'width' ? numValue : prev.width;
      const sqFt = length * width;
      const perimeter = 2 * (length + width);
      const updatedItems = prev.items.map(item => {
        if (item.item_type === 'deck_floor' || item.item_type === 'deck_prep_wash') {
          return { ...item, quantity: sqFt };
        }
        if (item.item_type === 'deck_rails_spindles') {
          return { ...item, quantity: perimeter };
        }
        return item;
      });
      return { ...updated, items: updatedItems };
    });
  }, []);

  const handleItemChange = useCallback((itemType, field, value) => {
    setDeck(prev => ({
      ...prev,
      items: prev.items.map(item => item.item_type === itemType ? { ...item, [field]: value } : item)
    }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await deckApi.update(projectId, deckId, {
        name: deck.name, length: deck.length, width: deck.width, items: deck.items, notes: deck.notes || "",
      });
      toast.success("Deck area saved");
      loadData();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const getDeckEstimate = () => {
    if (!estimate?.deck_breakdowns) return null;
    return estimate.deck_breakdowns.find(db => db.deck_id === deckId);
  };

  const renderItemGroup = (categoryName, itemTypes, skipHeading = false) => {
    const categoryItems = deck.items?.filter(item => itemTypes.includes(item.item_type)) || [];
    if (categoryItems.length === 0) return null;

    return (
      <div key={categoryName} className="space-y-3">
        {!skipHeading && <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium border-b border-border pb-2">{categoryName}</h3>}
        {categoryItems.map((item) => (
          <div key={item.item_type} className={`p-4 border rounded-sm transition-colors ${item.enabled ? 'border-amber-500/50 bg-card' : 'border-border bg-muted/20'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Switch checked={item.enabled} onCheckedChange={(checked) => handleItemChange(item.item_type, 'enabled', checked)} />
                <span className="font-medium text-sm">{ITEM_LABELS[item.item_type] || item.item_type}</span>
              </div>
              {item.enabled ? (
                <span className="text-xs px-2 py-1 rounded-sm bg-green-500/20 text-green-400 border border-green-500/30"><Check className="w-3 h-3 inline mr-1" />Included</span>
              ) : (
                <span className="text-xs px-2 py-1 rounded-sm bg-muted text-muted-foreground border border-border"><X className="w-3 h-3 inline mr-1" />Excluded</span>
              )}
            </div>
            {item.enabled && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="input-group">
                  <Label className="input-label text-xs">Qty ({ITEM_UNITS[item.item_type] || "units"})</Label>
                  <Input type="number" value={item.quantity} onChange={(e) => handleItemChange(item.item_type, 'quantity', parseFloat(e.target.value) || 0)} className="font-mono h-9" />
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Coats</Label>
                  <Select value={String(item.coats)} onValueChange={(v) => handleItemChange(item.item_type, 'coats', parseInt(v))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Coat</SelectItem>
                      <SelectItem value="2">2 Coats</SelectItem>
                      <SelectItem value="3">3 Coats</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Stain Type</Label>
                  <Select value={item.paint_type} onValueChange={(v) => handleItemChange(item.item_type, 'paint_type', v)}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deck_stain">Semi-Transparent Stain</SelectItem>
                      <SelectItem value="deck_solid">Solid Stain</SelectItem>
                      <SelectItem value="primer">Primer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Prep Level</Label>
                  <Select value={String(item.prep_level)} onValueChange={(v) => handleItemChange(item.item_type, 'prep_level', parseInt(v))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{PREP_LEVELS.map(c => <SelectItem key={c.value} value={String(c.value)}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="page-container flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!deck || !project) return null;

  const deckEstimate = getDeckEstimate();
  const moreItemsDefaultOpen = [
    ["Preparation", DECK_CATEGORIES.prep],
    ["Structures & Extras", DECK_CATEGORIES.structures],
  ].filter(([, itemTypes]) => deck.items?.some(i => itemTypes.includes(i.item_type) && i.enabled))
   .map(([label]) => label);

  return (
    <div className="page-container">
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/project/${projectId}`)}><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">{deck.name}</h1>
              <p className="text-xs text-muted-foreground">{project.name} • Deck Staining</p>
            </div>
          </div>
          <Button onClick={handleSave} className="btn-primary" disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save"}</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Deck Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="input-group">
                  <Label className="input-label">Deck Name</Label>
                  <Input value={deck.name} onChange={(e) => setDeck({ ...deck, name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group"><Label className="input-label">Length (ft)</Label><Input type="number" value={deck.length} onChange={(e) => handleDimensionChange('length', e.target.value)} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Width (ft)</Label><Input type="number" value={deck.width} onChange={(e) => handleDimensionChange('width', e.target.value)} className="font-mono" /></div>
                </div>
                <div className="p-3 bg-secondary/50 rounded-sm"><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Area</p><p className="font-mono text-lg">{(deck.length * deck.width).toFixed(0)} sq ft</p></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Deck Staining Items</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                {renderItemGroup("Deck Surfaces", DECK_CATEGORIES.surfaces)}
                <Accordion type="multiple" defaultValue={moreItemsDefaultOpen} className="space-y-0">
                  {[
                    ["Preparation", DECK_CATEGORIES.prep],
                    ["Structures & Extras", DECK_CATEGORIES.structures],
                  ].map(([label, itemTypes]) => {
                    const enabledCount = deck.items?.filter(i => itemTypes.includes(i.item_type) && i.enabled).length || 0;
                    return (
                      <AccordionItem key={label} value={label}>
                        <AccordionTrigger className="text-xs uppercase tracking-widest text-muted-foreground font-medium hover:no-underline">
                          <span>{label}{enabledCount > 0 && <span className="ml-2 text-amber-500 normal-case">({enabledCount} included)</span>}</span>
                        </AccordionTrigger>
                        <AccordionContent>{renderItemGroup(label, itemTypes, true)}</AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Notes</CardTitle></CardHeader>
              <CardContent>
                <Textarea value={deck.notes || ""} onChange={(e) => setDeck({ ...deck, notes: e.target.value })} placeholder="Add notes..." rows={3} />
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="sticky-panel">
              <CardHeader><CardTitle className="font-heading text-lg">Deck Estimate</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {deckEstimate ? (
                  <>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Dimensions</span><span className="font-mono text-sm">{deckEstimate.dimensions}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Square Feet</span><span className="font-mono">{deckEstimate.sq_ft} sq ft</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Labor Hours</span><span className="font-mono">{deckEstimate.total_hours} hrs</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Labor Cost</span><span className="font-mono">{formatCurrency(deckEstimate.labor_cost)}</span></div>
                    </div>
                    {Object.keys(deckEstimate.material_gallons || {}).length > 0 && (
                      <div className="border-t border-border pt-4">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Materials</p>
                        <div className="space-y-2">
                          {Object.entries(deckEstimate.material_gallons).map(([type, gallons]) => (
                            <div key={type} className="flex justify-between items-center text-sm"><span className="capitalize">{type.replace('_', ' ')}</span><span className="font-mono">{gallons} gal</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="border-t border-border pt-4">
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Material Cost</span><span className="font-mono">{formatCurrency(deckEstimate.material_cost)}</span></div>
                    </div>
                    <div className="border-t border-border pt-4">
                      <div className="flex justify-between items-center"><span className="font-heading font-semibold">Deck Total</span><span className="font-mono text-xl text-amber-500">{formatCurrency(deckEstimate.labor_cost + deckEstimate.material_cost)}</span></div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4"><p className="text-muted-foreground text-sm">Save to see estimate</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
